import React from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ExpSnackbarAlerts from '../../../../shared/components/display/ExpSnackbarAlerts';
import { useUserPreferences } from '../../../../shared/contexts/UserPreferencesContext';
import { DATE_FORMAT_OPTIONS } from '../../../../shared/hooks/useDateFormat';
import dayjs from 'dayjs';

const UserPreferences = () => {
  const { preferences, updatePreferences, loading } = useUserPreferences();

  // Local draft so the toggle is responsive; only persisted on Save
  const [mode, setMode] = React.useState(preferences.theme || 'dark');
  const [dateFormat, setDateFormat] = React.useState(
    preferences.dateFormat || 'DD.MM.YYYY'
  );
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'info',
  });

  // Sync local draft when the context finishes loading
  React.useEffect(() => {
    if (!loading) {
      setMode(preferences.theme || 'dark');
      setDateFormat(preferences.dateFormat || 'DD.MM.YYYY');
    }
  }, [loading, preferences.theme, preferences.dateFormat]);

  const handleChange = (event, newMode) => {
    if (newMode !== null) {
      setMode(newMode);
    }
  };

  const handleSave = async () => {
    try {
      await updatePreferences({ theme: mode, dateFormat });
      setSnackbar({
        open: true,
        message: 'Preferences updated successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating preferences:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save preferences. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <>
      <Box display='flex' alignItems='center' marginBottom={2}>
        <Typography marginRight={1} variant='h4'>
          Mode
        </Typography>
        <ToggleButtonGroup
          color='primary'
          value={mode}
          exclusive
          onChange={handleChange}
          aria-label='Mode'
        >
          <ToggleButton value='dark'>
            <DarkModeOutlinedIcon />
          </ToggleButton>
          <ToggleButton value='light'>
            <LightModeOutlinedIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box display='flex' alignItems='center' marginBottom={2}>
        <Typography marginRight={1} variant='h4'>
          Date Format
        </Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id='date-format-label'>Date Format</InputLabel>
          <Select
            labelId='date-format-label'
            value={dateFormat}
            label='Date Format'
            onChange={(e) => setDateFormat(e.target.value)}
          >
            {DATE_FORMAT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label} ({dayjs().format(opt.value)})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Button variant='outlined' onClick={handleSave}>
        <SaveOutlinedIcon /> Save
      </Button>
      <ExpSnackbarAlerts
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default UserPreferences;
